import { css, createGlobalStyle } from "styled-components";

const fontList = [
  ["regular", 500],
  ["medium", 600],
  ["bold", 700],
];

function getTalPublicAssetsPath() {
  return typeof process === "undefined"
    ? window["oetal"].env.talPublicAssetsPath
    : `${process.env["TAL_PUBLIC_ASSETS_ORIGIN"]}/static/tal-client/`;
}

/**
 * Emits @font-face blocks under both the new "origin-gordita" name (ODS's
 * current name) and the old "gordita" name — tal-core's index.html.tsx still
 * preloads the old filenames directly and its E2E specs assert a font named
 * "gordita" loads, so the old name/files stay available until tal-core is
 * updated separately. Matches ODS's own fallback chain (origin-ui's
 * typography.ts lists ["origin-gordita", "gordita", ...]).
 */
const GorditaFontStyles = () => {
  return fontList.flatMap(([style, weight]) => {
    const fontName = `gordita${style}-webfont`;
    return [
      css`
        @font-face {
          font-family: "origin-gordita";
          src: url("${getTalPublicAssetsPath()}fonts/gordita/origin-${fontName}.woff2")
              format("woff2"),
            url("${getTalPublicAssetsPath()}fonts/gordita/origin-${fontName}.woff")
              format("woff");
          font-weight: ${weight};
          font-style: ${style};
        }
      `,
      css`
        @font-face {
          font-family: "gordita";
          src: url("${getTalPublicAssetsPath()}fonts/gordita/${fontName}.woff2")
              format("woff2"),
            url("${getTalPublicAssetsPath()}fonts/gordita/${fontName}.woff")
              format("woff");
          font-weight: ${weight};
          font-style: ${style};
        }
      `,
    ];
  });
};

export const FontStyles = createGlobalStyle`
  ${GorditaFontStyles()}

  @font-face {
    font-family: "originicons";
    src: url('${getTalPublicAssetsPath()}fonts/originicons/originicons.woff') format('woff');
    font-weight: 400;
    font-style: normal;
  }
`;

export const ResetStyles = createGlobalStyle`
  * {
    font-family: 'origin-gordita', 'gordita', Arial, sans-serif;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
  }
  html, body, #root {
    height: 100%;
  }
  body {
    margin: 0;
    padding: 0;
  }
  body, h1, h2, h3, p, span {
    font-weight: normal;
  }
  #root {
    display: flex;
    flex-direction: column;
  }
  /*
   * Third-party tracking pixels (e.g. Amobee, injected by Adobe Launch) are
   * appended to <body> as inline <img> elements; their line box overflows the
   * 100%-height layout and adds a scrollbar. Take them out of flow without
   * using display:none, which can stop the beacon firing. top/left pin the
   * pixel inside the viewport, otherwise its static position (below the
   * 100%-height content) still extends the scrollable area.
   */
  body > img[width="1"][height="1"] {
    position: absolute;
    top: 0;
    left: 0;
    width: 1px;
    height: 1px;
    clip-path: inset(50%);
    pointer-events: none;
  }
  /*
   * This is likely no-longer required, and can be removed.

   * We recommend to apply this class on distorted animated elements in android webviews, eg. MuiDialog container.
   * Explanation: translate3d(0, 0, 0) can trigger hardware acceleration in Android WebView,
   * even if the WebView's hardware acceleration is set to false at the system level.
   */
  .accelerated-element {
    -webkit-transform: translate3d(0, 0, 0);
    will-change: transform;
  }
`;

export const IconFontStyles = createGlobalStyle`
  @font-face{
    font-family: "Material Icons";
    src:
        url('${getTalPublicAssetsPath()}fonts/materialIcons/MaterialIcons-Regular.woff2') format('woff2'),
        url('${getTalPublicAssetsPath()}fonts/materialIcons/MaterialIcons-Regular.woff') format('woff');
    font-weight: 400;
    font-style: normal;
  }

  .md-icon {
    font-family: 'Material Icons', Arial, sans-serif;
    font-weight: normal;
    font-style: normal;
    font-size: 24px;
    /* Preferred icon size */
    display: inline-block;
    line-height: 1;
    text-transform: none;
    letter-spacing: normal;
    word-wrap: normal;
    white-space: nowrap;

    vertical-align: bottom;
    margin-right: 5px;

    direction: ltr;
    /* Support for all WebKit browsers. */
    -webkit-font-smoothing: antialiased;
    /* Support for Safari and Chrome. */
    text-rendering: optimizeLegibility;
    /* Support for Firefox. */
    -moz-osx-font-smoothing: grayscale;
    /* Support for IE. */
    font-feature-settings: 'liga';
  }
`;

/*
 * Braze styling - Shared structure and layout
 * Contains all non-colour styling for Braze modals
 */
const BrazeSharedStyles = css`
  #braze-oe .ab-message-header,
  #braze-oe .ab-message-text {
    font-family: origin-gordita, gordita, Arial, sans-serif;
    text-align: left;
    font-weight: 400;
  }

  #braze-oe .ab-message-header {
    font-size: 20px;
    margin-bottom: 32px;
    font-weight: 600;
  }

  #braze-oe .ab-in-app-message.ab-background.ab-modal-interactions.ab-modal {
    display: flex;
    flex-direction: column;
    max-height: calc(100% - 64px);
  }
  #braze-oe .ab-message-text.ab-with-buttons {
    white-space: wrap !important;
    margin-bottom: 0;
  }

  #braze-oe .ab-message-buttons {
    padding: 32px 24px;
    text-align: center; /* Centers the buttons */
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  #braze-oe .ab-message-button {
    padding: 12px 24px;
    border-radius: 24px;
    font: 600 16px/24px origin-gordita, gordita, Arial, sans-serif;
    border: none;
    width: 100%;
    height: auto;
    background-color: #ffb233; /* Yellow button */
    color: #232323;
  }

  #braze-oe .ab-message-button:hover {
    background-color: #ffd185 !important;
    opacity: 1;
  }

  @media (max-width: 600px) {
    #braze-oe .ab-in-app-message.ab-slideup {
      max-width: calc(100% - 52px);
      min-width: calc(100% - 52px);
      margin: 16px;
    }
  }
`;

/*
 * Braze styling - Light mode colors
 */
export const BrazeStyles = createGlobalStyle`
  ${BrazeSharedStyles}


  #braze-oe .ab-message-button:nth-child(2) {
    background-color: #FFFFFF; /* White button */
    color: #232323;
    border: 1px solid #ffb233;
  }
  #braze-oe .ab-message-button:nth-child(2):hover {
    background-color: rgba(255, 96, 33, 0.08) !important;
  }
`;

/*
 * Braze styling - Dark mode colours
 * Needs extra styling for dark mode elements
 *  Should overwrite default Braze styles
 */
export const BrazeStylesDark = createGlobalStyle`
  ${BrazeSharedStyles}

  #braze-oe .ab-background {
    background-color: #1d1d1d !important;
  }

  /* text colour to white */
  #braze-oe .ab-message-text, #braze-oe .ab-message-text > * {
    color: #FFFFFF !important;
  }

  /* Outlined button styles */
  #braze-oe .ab-message-button:nth-child(2) {
    background-color: transparent !important;
    color: #FFFFFF !important;
    border: 1px solid #ffb233;
  }
  #braze-oe .ab-message-button:nth-child(2):hover {
    background-color: rgba(255, 255, 255, 0.08) !important;
  }
`;
